/**
 * Pay for Service page - payment for services interface
 */
import { useState, useEffect } from 'react';
import { useCompany } from '@/contexts/CompanyContext';
import { useCart } from '@/contexts/CartContext';
import { getServiceTypes } from '@/services/api/serviceTypeService';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import {
  WelcomeMessage,
  ServiceTypeCard,
  Cart,
  AmountSelectionModal,
} from '@/components/features/services';
import type { ServiceType } from '@/types/service.types';

export function PayForServicePage() {
  const { companyIdNumber } = useCompany();
  const { addItem, clearCartAndSetPurpose, scrollToCart } = useCart();

  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Clear cart and set purpose to service (2) on component mount
  useEffect(() => {
    clearCartAndSetPurpose(2);
  }, [clearCartAndSetPurpose]);

  // Load service types and services
  useEffect(() => {
    const loadData = async () => {
      if (!companyIdNumber) return;

      try {
        setIsLoading(true);
        setError(null);

        // Load donation types that should show on web
        const serviceTypesData = await getServiceTypes(companyIdNumber);
        // Filter to only show web-enabled donations
        const webServices = serviceTypesData.items.filter((dt) => dt.showWeb === 1);
        setServiceTypes(webServices);
      } catch (err) {
        console.error('Failed to load services data:', err);
        setError(err instanceof Error ? err : new Error('Failed to load services data'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [companyIdNumber]);

  const handleSelectService = (service: ServiceType) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleAddToCart = (amount: number) => {
    if (!selectedService) return;

    addItem({
      donationType: undefined,
      serviceType: selectedService,
      amount,
      purposeId: 2, // Service
    });

    // Scroll to cart on mobile after adding
    scrollToCart();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading donation options..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-2/3">
          <ErrorMessage error={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <WelcomeMessage />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Area */}
        <div className="lg:col-span-2 bg-emerald-50 p-2 rounded-xl shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Select the service that you would like to pay for
          </h2>
          {serviceTypes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No service types available at this time.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {serviceTypes.map((service) => (
                <ServiceTypeCard
                  key={service.donationType}
                  serviceType={service}
                  onSelect={handleSelectService}
                />
              ))}
            </div>
          )}
        </div>
        {/* Cart Sidebar */}
        <div className="lg:col-span-1">
          <Cart />
        </div>
      </div>

      {/* Amount Selection Modal */}
      {selectedService && (
        <AmountSelectionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          serviceType={selectedService}
          onAddToCart={handleAddToCart}
        />
      )}
    </div>
  );
}
